/**
 * A dashboard section's focus (spec §1.6; card D1): subscribes the section to the shell's focus inputs, runs a
 * `DashFocus` over the section's groups and provides it as `FocusContext`, so `FocusButton` / `useFocusable` from
 * `ui/focus.tsx` work inside the dashboard exactly as in the station UI (hover and click move the focus, H9).
 *
 *   const focus = useDashFocus(props.nav, groups)
 *   return <FocusContext.Provider value={focus}>…<FocusButton group="list" index={i} …/></FocusContext.Provider>
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FocusGroup } from '../nav'
import { loadDashboardConfig } from './config'
import { createDashFocus, type DashFocus, type DashNavPort } from './focus'

export function useDashFocus(nav: DashNavPort, groups: readonly FocusGroup[]): DashFocus {
  const latest = useRef(groups)
  latest.current = groups
  const [, setTick] = useState(0)
  const focus = useMemo(
    () =>
      createDashFocus({
        groups: () => latest.current,
        pageRows: loadDashboardConfig().focusPageRows,
        onChange: () => setTick((n) => n + 1),
      }),
    [],
  )
  useEffect(() => nav.subscribe((input) => focus.handle(input)), [nav, focus])
  return focus
}
